import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Sidebar from '../components/Sidebar';
import ArticleCard from '../components/ArticleCard';
import AdSenseSlot from '../components/AdSenseSlot';
import Meta from '../components/Meta';
import SocialShareBar from '../components/SocialShareBar';
import TipButton from '../components/TipButton';
import IGStoryShare from '../components/IGStoryShare';
import DataVisualization from '../components/DataVisualization';
import ArticleQuizEmbed from '../components/ArticleQuizEmbed';
import { AdInArticle } from '../components/AdSenseManaged';
import { useLanguage } from '../context/LanguageContext';
import { Clock, Eye, Heart, Share2, Facebook, Twitter, Linkedin, Link2, ChevronUp, ChevronLeft, ChevronRight, MessageCircle, User, Send, Lock, Crown, Bookmark, BookmarkCheck, Volume2, VolumeX, Pause, Play, Download, QrCode, FileText, Reply, ThumbsUp, CreditCard } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { hr, de, enGB, es, fr, pt, zhCN, ar, hi, ru, ja } from 'date-fns/locale';

const DATE_LOCALES = { hr, de, en: enGB, es, fr, pt, zh: zhCN, ar, hi, ru, ja };
const getDateLocale = (lang) => DATE_LOCALES[lang] || hr;

const API = process.env.REACT_APP_BACKEND_URL;

// Reading progress bar with red glow and percentage
function ReadingProgress({ articleRef }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let maxDepth = 0;
    const sessionId = `s_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    const updateProgress = () => {
      if (!articleRef.current) return;
      const el = articleRef.current;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight;
      const scrolled = -rect.top;
      const p = Math.max(0, Math.min(100, (scrolled / (total - window.innerHeight)) * 100));
      setProgress(p);
      if (p > maxDepth + 10) {
        maxDepth = p;
        const slug = window.location.pathname.split('/article/')[1];
        if (slug) {
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/articles/${slug}/reading-depth`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ depth: Math.round(p), session_id: sessionId }),
          }).catch(() => {});
        }
      }
    };
    window.addEventListener('scroll', updateProgress, { passive: true });
    return () => window.removeEventListener('scroll', updateProgress);
  }, [articleRef]);

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] h-1" data-testid="reading-progress">
      <div
        className="h-full transition-all duration-150 ease-out"
        style={{
          width: `${progress}%`,
          background: 'linear-gradient(90deg, #dc2626, #ef4444)',
          boxShadow: '0 0 8px rgba(220, 38, 38, 0.6), 0 0 16px rgba(220, 38, 38, 0.3)',
        }}
      />
      {progress > 0 && progress < 100 && (
        <span
          className="absolute top-1.5 text-[10px] font-bold text-red-600 bg-white dark:bg-gray-900 px-1.5 py-0.5 rounded-full shadow-sm border border-red-200"
          style={{ left: `min(calc(${progress}% - 16px), calc(100% - 40px))` }}
          data-testid="reading-progress-percent"
        >
          {Math.round(progress)}%
        </span>
      )}
    </div>
  );
}

// Table of Contents - sticky left sidebar
function TableOfContents({ content }) {
  const [activeId, setActiveId] = useState('');
  const headings = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(content || '', 'text/html');
  doc.querySelectorAll('h2, h3').forEach((el, i) => {
    headings.push({ id: `heading-${i}`, text: el.textContent, level: parseInt(el.tagName[1]) });
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-80px 0px -60% 0px' }
    );
    headings.forEach(h => {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [content]);

  if (headings.length < 2) return null;

  return (
    <nav className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm" data-testid="table-of-contents">
      <h4 className="font-serif font-bold text-navy dark:text-white text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
        <span className="w-1 h-4 bg-red-500 rounded-full" />
        Sadržaj
      </h4>
      <ol className="space-y-1.5">
        {headings.map((h, i) => (
          <li key={i} className={`${h.level === 3 ? 'ml-3' : ''}`}>
            <a href={`#${h.id}`}
              className={`block text-xs leading-snug py-1 px-2 rounded transition-all ${activeId === h.id ? 'text-red-600 font-semibold bg-red-50 dark:bg-red-900/10' : 'text-gray-500 dark:text-gray-400 hover:text-navy dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
              {h.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

// Advanced Gallery with Lightbox
function GallerySection({ images }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const openLightbox = (index) => { setActiveIndex(index); setLightboxOpen(true); };
  const closeLightbox = () => setLightboxOpen(false);
  const prevImage = () => setActiveIndex(i => (i - 1 + images.length) % images.length);
  const nextImage = () => setActiveIndex(i => (i + 1) % images.length);

  return (
    <div className="mt-8" data-testid="article-gallery">
      <h3 className="font-serif text-xl font-bold text-navy dark:text-white mb-4">Galerija</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {images.map((img, i) => (
          <div key={i} className="relative group cursor-pointer overflow-hidden rounded-lg" onClick={() => openLightbox(i)}>
            <img src={img.url} alt={`Slika ${i + 1}`} className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-300" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <Eye size={24} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" data-testid="gallery-lightbox" onClick={closeLightbox}>
          <button onClick={(e) => { e.stopPropagation(); closeLightbox(); }} className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl z-10 w-10 h-10 flex items-center justify-center">&times;</button>
          <button onClick={(e) => { e.stopPropagation(); prevImage(); }} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10">
            <ChevronLeft size={28} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); nextImage(); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10">
            <ChevronRight size={28} />
          </button>
          <div className="max-w-5xl max-h-[85vh] px-4" onClick={e => e.stopPropagation()}>
            <img src={images[activeIndex]?.url} alt={`Slika ${activeIndex + 1}`} className="max-w-full max-h-[80vh] object-contain mx-auto rounded-lg" />
            <p className="text-center text-white/60 text-sm mt-3">{activeIndex + 1} / {images.length}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams();

  // Process content to add heading IDs for TOC navigation and insert ads between paragraphs
  const processContent = (html) => {
    if (!html) return '<p>Sadržaj nije dostupan.</p>';
    let counter = 0;
    let processed = html.replace(/<(h[23])([^>]*)>/gi, (match, tag, attrs) => {
      const id = `heading-${counter++}`;
      if (attrs.includes('id=')) return match;
      return `<${tag}${attrs} id="${id}">`;
    });
    // Insert ad placeholder after every 3rd paragraph (Google AdSense best practice)
    if (!hideAds) {
      let pCount = 0;
      processed = processed.replace(/<\/p>/gi, () => {
        pCount++;
        if (pCount === 3 || pCount === 7) {
          return `</p><div class="adsense-in-text" data-ad-slot="in-text-${pCount}"></div>`;
        }
        return '</p>';
      });
    }
    return processed;
  };
  const { user, isAuthenticated } = useAuth0();
  const { lang: globalLang } = useLanguage();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [paywalled, setPaywalled] = useState(false);
  const [meterInfo, setMeterInfo] = useState(null);
  const [saved, setSaved] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [articleLang, setArticleLang] = useState('hr');
  const [availableLangs, setAvailableLangs] = useState(['hr']);
  const [abTitle, setAbTitle] = useState(null);
  const [abTestId, setAbTestId] = useState(null);
  const [abVariantId, setAbVariantId] = useState(null);
  const [recentArticles, setRecentArticles] = useState([]);
  const [popularArticles, setPopularArticles] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [hideAds, setHideAds] = useState(false);
  const articleRef = useRef(null);
  const [categoryColor, setCategoryColor] = useState('');
  const [ttsPlaying, setTtsPlaying] = useState(false);
  const [ttsText, setTtsText] = useState('');
  const [ttsLoading, setTtsLoading] = useState(false);
  const [reactions, setReactions] = useState({});
  const [userReactions, setUserReactions] = useState([]);
  const [readingTime, setReadingTime] = useState(0);
  const [replyTo, setReplyTo] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [micropayAccess, setMicropayAccess] = useState(null);
  const [crossPortalLinks, setCrossPortalLinks] = useState([]);
  const REACTION_EMOJIS = { heart: '❤️', wow: '😮', sad: '😢', clap: '👏', fire: '🔥' };

  useEffect(() => {
    if (slug) {
      loadArticle();
      loadSidebar();
      // Verify micropayment purchase from URL params
      const params = new URLSearchParams(window.location.search);
      const pid = params.get('pid');
      const purchaseStatus = params.get('purchase');
      if (pid && purchaseStatus === 'success') {
        axios.get(`${API}/api/micropayments/verify/${pid}`).then(r => {
          if (r.data?.verified) { setPaywalled(false); setMicropayAccess(prev => prev ? { ...prev, has_access: true, reason: 'purchased' } : prev); }
        }).catch(() => {});
        // Clean URL
        window.history.replaceState({}, '', `/article/${slug}`);
      }
      // Check subscription for ad-free
      const localUser = JSON.parse(localStorage.getItem('ct_user') || 'null');
      if (localUser?.email) {
        axios.get(`${API}/api/user/has-subscription?email=${encodeURIComponent(localUser.email)}`)
          .then(r => { if (r.data?.has_subscription) setHideAds(true); }).catch(() => {});
      }
      // Track page visit
      const sessionId = sessionStorage.getItem('ct_session') || (() => { const sid = Math.random().toString(36).slice(2); sessionStorage.setItem('ct_session', sid); return sid; })();
      axios.post(`${API}/api/analytics/track`, { session_id: sessionId, url: `/article/${slug}`, page_title: document.title }).catch(() => {});
      // Load category colors
      axios.get(`${API}/api/categories`).then(r => {
        const cats = r.data?.categories || [];
        window.__ct_categories = cats;
      }).catch(() => {});
    }
  }, [slug]);

  // React to global language change
  useEffect(() => {
    if (!slug || !article) return;
    if (globalLang === 'hr') {
      loadArticle();
      setArticleLang('hr');
    } else if (availableLangs.includes(globalLang)) {
      axios.get(`${API}/api/articles/${slug}/translation/${globalLang}`)
        .then(res => {
          setArticle(prev => ({ ...prev, title: res.data.title, excerpt: res.data.excerpt, content: res.data.content }));
          setArticleLang(globalLang);
        }).catch(() => {});
    }
  }, [globalLang]);

  const loadArticle = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/articles/${slug}?lang=${globalLang}`);
      setArticle(res.data);
      setLikes(res.data.like_count || 0);

      // Load A/B test title
      try {
        const abRes = await axios.get(`${API}/api/articles/${slug}/ab-title`);
        if (abRes.data?.variant_id) {
          setAbTitle(abRes.data.title);
          setAbTestId(abRes.data.test_id);
          setAbVariantId(abRes.data.variant_id);
        }
      } catch {}

      // Load available translations
      try {
        const transRes = await axios.get(`${API}/api/articles/${slug}/translations`);
        setAvailableLangs(transRes.data?.available_languages || ['hr']);
      } catch {}

      // Check paywall
      const localUser = JSON.parse(localStorage.getItem('ct_user') || 'null');
      if (res.data.is_premium || res.data.is_special) {
        if (res.data.is_special) {
          // Special article: 5 free per month for logged-in users
          const userEmail = localUser?.email || user?.email || '';
          if (!userEmail) {
            setPaywalled(true);
          } else {
            try {
              const accessRes = await axios.get(`${API}/api/articles/check-premium-access?article_id=${res.data.id || ''}&user_email=${encodeURIComponent(userEmail)}`);
              setPaywalled(!accessRes.data.can_access);
              setMeterInfo(accessRes.data);
            } catch { setPaywalled(true); }
          }
        } else {
          try {
            const accessRes = await axios.get(`${API}/api/articles/${slug}/access`, {
              headers: localUser?.email ? { 'X-User-Email': localUser.email } : {},
            });
            setPaywalled(!accessRes.data.access);
          } catch { setPaywalled(true); }
        }
      } else {
        setPaywalled(false);
      }

      const relRes = await axios.get(`${API}/api/articles/related/${slug}?limit=8`);
      setRelated(relRes.data?.articles || relRes.data || []);

      // Check micropayment access for premium articles
      const lu2 = JSON.parse(localStorage.getItem('ct_user') || 'null');
      axios.get(`${API}/api/micropayments/check-access/${slug}`, {
        headers: lu2?.email ? { 'X-User-Email': lu2.email } : {}
      }).then(r => setMicropayAccess(r.data)).catch(() => {});

      // Load cross-portal related articles
      axios.get(`${API}/api/cross-portal/related/${slug}?limit=4`)
        .then(r => setCrossPortalLinks(r.data?.related || []))
        .catch(() => {});

      const commRes = await axios.get(`${API}/api/comments/threaded/${res.data.id || res.data._id}`);
      setComments(commRes.data?.comments || []);

      // Load reactions
      const lu = JSON.parse(localStorage.getItem('ct_user') || 'null');
      axios.get(`${API}/api/articles/${slug}/reactions?user_email=${encodeURIComponent(lu?.email || '')}`).then(r => {
        setReactions(r.data?.reactions || {});
        setUserReactions(r.data?.user_reactions || []);
      }).catch(() => {});

      // Calculate reading time
      const ct = (res.data.content || '').replace(/<[^>]+>/g, '');
      const wc = ct.split(/\s+/).length;
      setReadingTime(Math.max(1, Math.ceil(wc / 200)));

      // Track reading history for personalized feed
      const trackUser = JSON.parse(localStorage.getItem('ct_user') || 'null');
      if (trackUser?.email || user?.email || user?.sub) {
        axios.post(`${API}/api/reading-history/track?article_slug=${encodeURIComponent(slug)}&user_email=${encodeURIComponent(trackUser?.email || user?.email || '')}&user_sub=${encodeURIComponent(user?.sub || '')}`).catch(() => {});
      }
    } catch (err) {
      console.error('Failed to load article:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSidebar = async () => {
    try {
      const [rec, pop, tagsRes] = await Promise.all([
        axios.get(`${API}/api/articles?limit=5&status=published`),
        axios.get(`${API}/api/articles?limit=5&sort=views&status=published`),
        axios.get(`${API}/api/tags?limit=30`),
      ]);
      setRecentArticles(rec.data?.articles || rec.data || []);
      setPopularArticles(pop.data?.articles || pop.data || []);
      setTags(tagsRes.data?.tags || tagsRes.data || []);
    } catch {}
  };

  const handleLike = async () => {
    if (!article) return;
    try {
      const res = await axios.post(`${API}/api/articles/${article.id || article._id}/like`);
      setLikes(res.data.like_count || likes + (liked ? -1 : 1));
      setLiked(!liked);
    } catch {}
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = article?.title || '';
    const links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    };
    if (links[platform]) window.open(links[platform], '_blank', 'width=600,height=400');
    if (platform === 'copy') {
      navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !isAuthenticated) return;
    setSubmittingComment(true);
    try {
      const lu = JSON.parse(localStorage.getItem('ct_user') || 'null');
      const res = await axios.post(`${API}/api/comments/moderated`, {
        article_id: article.id || article._id || slug,
        content: newComment.trim(),
        author_name: user?.name || lu?.name || 'Anonymous',
        author_email: user?.email || lu?.email || '',
        author_picture: user?.picture || lu?.picture || '',
        user_sub: user?.sub || '',
        parent_id: replyTo,
      });
      if (replyTo) {
        setComments(prev => prev.map(c => c.id === replyTo ? { ...c, replies: [...(c.replies || []), res.data] } : c));
      } else {
        setComments(prev => [res.data, ...prev]);
      }
      setNewComment('');
      setReplyTo(null);
    } catch (err) {
      alert('Failed to post comment.');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleTTS = async () => {
    if (ttsPlaying) {
      window.speechSynthesis.cancel();
      setTtsPlaying(false);
      return;
    }
    setTtsLoading(true);
    try {
      if (!ttsText) {
        const res = await axios.get(`${API}/api/articles/${slug}/tts`);
        setTtsText(res.data?.text || '');
        speakText(res.data?.text || '');
      } else {
        speakText(ttsText);
      }
    } catch { /* fallback: use article content */ 
      const text = (article.content || '').replace(/<[^>]+>/g, '').slice(0, 3000);
      speakText(text);
    }
    setTtsLoading(false);
  };

  const speakText = (text) => {
    if (!text || !window.speechSynthesis) return;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = articleLang === 'hr' ? 'hr-HR' : articleLang === 'de' ? 'de-DE' : 'en-US';
    utterance.rate = 0.95;
    utterance.onend = () => setTtsPlaying(false);
    window.speechSynthesis.speak(utterance);
    setTtsPlaying(true);
  };

  const handleReaction = async (type) => {
    const lu = JSON.parse(localStorage.getItem('ct_user') || 'null');
    try {
      const res = await axios.post(`${API}/api/articles/${slug}/react`, {
        article_id: slug, reaction_type: type, user_email: lu?.email || user?.email || ''
      });
      if (res.data?.action === 'added') {
        setReactions(prev => ({ ...prev, [type]: (prev[type] || 0) + 1 }));
        setUserReactions(prev => [...prev, type]);
      } else {
        setReactions(prev => ({ ...prev, [type]: Math.max(0, (prev[type] || 0) - 1) }));
        setUserReactions(prev => prev.filter(r => r !== type));
      }
    } catch {}
  };

  const handleWhatsAppShare = () => {
    const url = window.location.href;
    const text = `${article?.title || ''} - ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  if (loading) {
    return (
      <div className="min-h-screen bg-cream dark:bg-gray-900">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-3 space-y-4">
              <div className="skeleton h-8 w-3/4 rounded" />
              <div className="skeleton aspect-video rounded-xl" />
              <div className="space-y-2">{[...Array(8)].map((_, i) => <div key={i} className="skeleton h-4 rounded" />)}</div>
            </div>
            <div className="lg:col-span-1 space-y-4">
              {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-48 rounded-xl" />)}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) {
    const notFoundTexts = {
      hr: { title: 'Članak nije pronađen', desc: 'Članak koji tražite ne postoji ili je uklonjen.', btn: 'Natrag na početnu' },
      en: { title: 'Article Not Found', desc: "The article you're looking for doesn't exist or has been removed.", btn: 'Back to Homepage' },
      de: { title: 'Artikel nicht gefunden', desc: 'Der gesuchte Artikel existiert nicht oder wurde entfernt.', btn: 'Zurück zur Startseite' },
      es: { title: 'Artículo no encontrado', desc: 'El artículo que buscas no existe o ha sido eliminado.', btn: 'Volver al inicio' },
      fr: { title: 'Article introuvable', desc: "L'article que vous recherchez n'existe pas ou a été supprimé.", btn: "Retour à l'accueil" },
      pt: { title: 'Artigo não encontrado', desc: 'O artigo que procura não existe ou foi removido.', btn: 'Voltar à página inicial' },
      zh: { title: '文章未找到', desc: '您查找的文章不存在或已被删除。', btn: '返回首页' },
      ar: { title: 'المقال غير موجود', desc: 'المقال الذي تبحث عنه غير موجود أو تمت إزالته.', btn: 'العودة إلى الصفحة الرئيسية' },
      hi: { title: 'लेख नहीं मिला', desc: 'जो लेख आप खोज रहे हैं वह मौजूद नहीं है या हटा दिया गया है।', btn: 'होमपेज पर वापस जाएं' },
      ru: { title: 'Статья не найдена', desc: 'Статья, которую вы ищете, не существует или была удалена.', btn: 'На главную' },
      ja: { title: '記事が見つかりません', desc: 'お探しの記事は存在しないか、削除されました。', btn: 'ホームページに戻る' },
    };
    const nf = notFoundTexts[globalLang] || notFoundTexts['hr'];
    return (
      <div className="min-h-screen bg-cream dark:bg-gray-900">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <h1 className="font-serif text-4xl font-bold text-navy dark:text-white mb-4" data-testid="article-not-found-title">{nf.title}</h1>
          <p className="text-gray-500 mb-8">{nf.desc}</p>
          <Link to="/" className="px-6 py-3 bg-navy text-white rounded-lg hover:bg-navy-dark transition-colors" data-testid="back-to-home-btn">
            {nf.btn}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream dark:bg-gray-900 overflow-x-hidden">
      <Meta
        title={article.custom_meta_title || article.seo_title || article.title}
        description={article.custom_meta_description || article.meta_description || article.excerpt || article.title}
        image={article.custom_og_image || article.featured_image}
        type="article"
        article={{ published_at: article.published_at, author: article.author, category: article.category, tags: article.tags }}
        noindex={article.noindex}
        canonical={article.canonical_url}
        jsonLd={{
          "@context": "https://schema.org", "@type": "NewsArticle",
          headline: article.custom_meta_title || article.title,
          description: article.custom_meta_description || article.excerpt,
          image: article.custom_og_image || article.featured_image,
          datePublished: article.published_at, dateModified: article.updated_at || article.published_at,
          author: { "@type": "Person", name: article.author || "Croatia Times" },
          publisher: { "@type": "Organization", name: "Croatia Times" },
          mainEntityOfPage: { "@type": "WebPage", "@id": article.canonical_url || window.location.href },
        }}
      />
      <ReadingProgress articleRef={articleRef} />
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* TOC - Left column on desktop */}
          <div className="hidden lg:block lg:col-span-2">
            <div className="sticky top-16">
              <TableOfContents content={article.content} />
            </div>
          </div>

          {/* Article content */}
          <div className="lg:col-span-7">
            {/* Mobile TOC */}
            <div className="lg:hidden mb-6">
              <TableOfContents content={article.content} />
            </div>

            <article ref={articleRef} data-testid="article-content">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-xs text-gray-500 mb-4 font-accent uppercase tracking-wide">
                <Link to="/" className="hover:text-terracotta transition-colors">{globalLang === 'hr' ? 'Početna' : 'Home'}</Link>
                <span>/</span>
                {article.category && (
                  <>
                    <Link to={`/category/${article.category.toLowerCase()}`} className="hover:text-terracotta transition-colors">{article.category}</Link>
                    <span>/</span>
                  </>
                )}
                <span className="text-gray-400 truncate max-w-xs">{article.title}</span>
              </nav>

              {/* Category */}
              {article.category && (
                <Link to={`/category/${article.category.toLowerCase()}`}>
                  <span className="category-badge text-white px-3 py-1 rounded-full text-xs"
                    style={{ backgroundColor: (window.__ct_categories || []).find(c => c.name?.toLowerCase() === article.category?.toLowerCase())?.color || '#c2410c' }}>
                    {article.category}
                  </span>
                </Link>
              )}

              {/* Title (with A/B variant) */}
              <h1 className="font-serif text-3xl md:text-4xl font-bold text-navy dark:text-white mt-3 mb-4 leading-tight"
                onClick={() => { if (abTestId && abVariantId) { fetch(`${API}/api/ab-test/${abTestId}/click`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ variant_id: abVariantId }) }); } }}
                data-testid="article-title">
                {abTitle || article.title}
              </h1>

              {/* Language switcher */}
              {availableLangs.length > 1 && (
                <div className="flex items-center gap-1.5 mb-3 overflow-x-auto pb-1" data-testid="article-lang-switcher">
                  {!availableLangs.includes('hr') && (
                    <button onClick={async () => {
                      setArticleLang('hr');
                      const res = await axios.get(`${API}/api/articles/${slug}`);
                      setArticle(res.data);
                    }}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors flex-shrink-0 ${articleLang === 'hr' ? 'bg-navy text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200'}`}>
                      HR
                    </button>
                  )}
                  {availableLangs.map(l => (
                    <button key={l} onClick={async () => {
                      if (l === articleLang) return;
                      setArticleLang(l);
                      if (l === 'hr') { const res = await axios.get(`${API}/api/articles/${slug}`); setArticle(res.data); }
                      else { try { const res = await axios.get(`${API}/api/articles/${slug}/translation/${l}`); setArticle(prev => ({ ...prev, title: res.data.title, excerpt: res.data.excerpt, content: res.data.content })); } catch {} }
                    }}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors flex-shrink-0 ${articleLang === l ? 'bg-navy text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200'}`}>
                      {l.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}

              {/* Excerpt */}
              {article.excerpt && (
                <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed mb-4 italic border-l-4 border-terracotta pl-4">{article.excerpt}</p>
              )}

              {/* Meta info */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                {article.author && (
                  <Link to={`/author/${encodeURIComponent(article.author)}`} className="flex items-center gap-2 hover:text-sky-accent transition-colors">
                    <User size={14} />
                    <span className="font-medium text-navy dark:text-white">{article.author}</span>
                  </Link>
                )}
                {article.published_at && (
                  <div className="flex items-center gap-1">
                    <Clock size={14} />
                    <time>{format(new Date(article.published_at), 'd. MMMM yyyy.', { locale: getDateLocale(globalLang) })}</time>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Eye size={14} />
                  <span>{article.view_count || 0} {globalLang === 'hr' ? 'pregleda' : 'views'}</span>
                </div>
                {readingTime > 0 && (
                  <div className="flex items-center gap-1" data-testid="reading-time">
                    <Clock size={14} />
                    <span>{readingTime} min {globalLang === 'hr' ? 'čitanja' : 'read'}</span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <MessageCircle size={14} />
                  <span>{comments.length} {globalLang === 'hr' ? 'komentara' : 'comments'}</span>
                </div>
                {/* TTS Button */}
                <button
                  onClick={handleTTS}
                  data-testid="tts-button"
                  disabled={ttsLoading}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${ttsPlaying ? 'bg-red-100 text-red-600 dark:bg-red-900/20' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-sky-accent/10 hover:text-sky-accent'}`}
                >
                  {ttsPlaying ? <><VolumeX size={12} /> Stop</> : <><Volume2 size={12} /> {globalLang === 'hr' ? 'Slušaj' : 'Listen'}</>}
                </button>
              </div>

              {/* Share bar (top) */}
              <div className="mb-6 flex items-center gap-2 flex-wrap" data-testid="share-buttons">
                <SocialShareBar article={article} />
                <IGStoryShare slug={article.slug} title={article.title} />
              </div>

              {/* Featured Image */}
              {article.featured_image && (
                <figure className="mb-6">
                  <img src={article.featured_image} alt={article.title} className="w-full rounded-xl max-h-[500px] object-cover" />
                  {article.image_source_name && (
                    <figcaption className="text-xs text-gray-400 mt-1 text-right">
                      Image source: <a href={article.image_source_url || '#'} target="_blank" rel="noopener noreferrer" className="hover:text-terracotta">{article.image_source_name}</a>
                    </figcaption>
                  )}
                </figure>
              )}

              {/* In-content Ad */}
              {!hideAds && <div className="my-6 flex justify-center">
                <AdSenseSlot slotId="in-content-1" width={728} height={90} />
              </div>}

              {/* Metering banner for premium articles being viewed for free */}
              {!paywalled && meterInfo && meterInfo.reason === 'metered' && meterInfo.remaining < meterInfo.limit && (
                <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3 flex items-center justify-between" data-testid="meter-banner">
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    <span className="font-bold">{meterInfo.remaining}</span> of {meterInfo.limit} free premium articles remaining this month
                  </p>
                  <Link to="/subscription" className="text-xs font-bold text-terracotta hover:underline">Subscribe for unlimited</Link>
                </div>
              )}

              {/* Article Content */}
              {paywalled ? (
                <div className="relative" data-testid="paywall-overlay">
                  <div className="prose prose-lg max-w-none text-gray-800 dark:text-gray-200 article-body max-h-[200px] overflow-hidden"
                    dangerouslySetInnerHTML={{ __html: (article.content || '').substring(0, 500) + '...' }}
                    style={{ lineHeight: '1.8', fontSize: '1.0625rem' }} />
                  <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-cream dark:from-gray-900 to-transparent" />
                  <div className="relative bg-white dark:bg-gray-800 border-2 border-terracotta/30 rounded-2xl p-8 text-center mt-0 shadow-lg">
                    <div className="w-14 h-14 bg-terracotta/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Lock size={24} className="text-terracotta" />
                    </div>
                    <h3 className="font-serif text-2xl font-bold text-navy dark:text-white mb-2">Premium sadržaj</h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-4 max-w-md mx-auto">Ovaj članak je dostupan samo pretplatnicima.</p>
                    {meterInfo && meterInfo.reason === 'free_limit' && (
                      <p className="text-sm text-amber-600 dark:text-amber-400 mb-4" data-testid="meter-info">
                        You've used all {meterInfo.limit} free premium articles this month. Resets {meterInfo.resets_at ? new Date(meterInfo.resets_at).toLocaleDateString() : 'next month'}.
                      </p>
                    )}
                    {/* Micropayment option */}
                    {micropayAccess && !micropayAccess.has_access && micropayAccess.price_eur && (
                      <div className="mb-4" data-testid="micropayment-option">
                        <button
                          onClick={async () => {
                            const lu = JSON.parse(localStorage.getItem('ct_user') || 'null');
                            if (!lu?.email) { alert('Morate biti prijavljeni za kupnju članka'); return; }
                            try {
                              const res = await axios.post(`${API}/api/micropayments/purchase/${slug}`, {
                                origin_url: window.location.origin
                              }, { headers: { 'X-User-Email': lu.email } });
                              if (res.data.already_purchased) { window.location.reload(); return; }
                              if (res.data.checkout_url) window.location.href = res.data.checkout_url;
                            } catch (e) { alert('Greška pri kupnji: ' + (e.response?.data?.detail || e.message)); }
                          }}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors"
                          data-testid="buy-article-btn"
                        >
                          <CreditCard size={18} /> Kupi članak za {micropayAccess.price_eur} EUR
                        </button>
                        {micropayAccess.show_subscription_prompt && (
                          <p className="text-sm text-gray-400 mt-3">
                            {micropayAccess.subscription_prompt_text || 'Ili se pretplatite za neograničen pristup svim člancima!'}
                          </p>
                        )}
                        <div className="flex items-center gap-3 justify-center mt-3">
                          <div className="h-px bg-gray-200 dark:bg-gray-600 flex-1" />
                          <span className="text-xs text-gray-400">ili</span>
                          <div className="h-px bg-gray-200 dark:bg-gray-600 flex-1" />
                        </div>
                      </div>
                    )}
                    <Link to="/subscription" className="inline-flex items-center gap-2 px-6 py-3 bg-terracotta text-white rounded-xl font-medium hover:bg-terracotta-dark transition-colors">
                      <Crown size={18} /> Pretplatite se od \u20AC4.99/mj
                    </Link>
                  </div>
                </div>
              ) : (
              <>
              <div
                className="prose prose-lg max-w-none text-gray-800 dark:text-gray-200 article-body"
                dangerouslySetInnerHTML={{ __html: processContent(article.content) }}
                style={{
                  lineHeight: '1.8',
                  fontSize: '1.0625rem',
                }}
              />
              {/* Interactive Data Visualizations */}
              <DataVisualization content={article.content} />
              {/* Embedded Quiz */}
              <ArticleQuizEmbed articleId={article.id || article._id} slug={slug} />
              </>
              )}

              {/* In-article AdSense (between content and gallery) */}
              {!hideAds && <AdInArticle />}

              {/* Image Gallery */}
              {article.gallery && article.gallery.length > 0 && (
                <GallerySection images={article.gallery} />
              )}

              {/* Source attribution */}
              {article.source_name && (
                <div className="mt-6 p-4 bg-cream dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 text-sm" data-testid="article-source">
                  <p className="text-gray-600 dark:text-gray-400">
                    Source: <a href={article.source_link_url || article.source_url || '#'} target="_blank" rel="noopener noreferrer" className="text-terracotta hover:underline font-medium">{article.source_display_url || article.source_url || article.source_name}</a>
                  </p>
                  {article.image_source_name && (
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                      Photos: <a href={article.image_source_url || article.source_url || '#'} target="_blank" rel="noopener noreferrer" className="text-terracotta hover:underline font-medium">{article.image_source_name}</a>
                    </p>
                  )}
                </div>
              )}

              {/* Tags */}
              {article.tags && article.tags.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {article.tags.map(tag => (
                    <Link key={tag} to={`/tag/${encodeURIComponent(tag)}`} className="px-3 py-1 bg-cream dark:bg-gray-700 text-navy dark:text-gray-300 text-xs rounded-full hover:bg-navy hover:text-white transition-colors">
                      #{tag}
                    </Link>
                  ))}
                </div>
              )}

              {/* Like / Share / Reactions (bottom) */}
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                {/* Reactions Bar */}
                <div className="flex items-center gap-2 mb-4" data-testid="reactions-bar">
                  <span className="text-sm text-gray-500 font-display mr-1">{globalLang === 'hr' ? 'Reagiraj:' : 'React:'}</span>
                  {Object.entries(REACTION_EMOJIS).map(([type, emoji]) => (
                    <button key={type} onClick={() => handleReaction(type)} data-testid={`reaction-${type}`}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-all ${userReactions.includes(type) ? 'bg-sky-accent/10 ring-2 ring-sky-accent/40 scale-105' : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 hover:scale-105'}`}>
                      <span>{emoji}</span>
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-300">{reactions[type] || 0}</span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button onClick={handleLike} data-testid="like-button"
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${liked ? 'bg-terracotta text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-terracotta hover:text-white'}`}>
                      <Heart size={16} fill={liked ? 'currentColor' : 'none'} />
                      <span>{likes}</span>
                    </button>
                    <TipButton articleId={article.slug || article.id} articleTitle={article.title} />
                    <button onClick={async () => {
                      const lu = JSON.parse(localStorage.getItem('ct_user') || 'null');
                      if (!lu?.email) return;
                      if (bookmarked) {
                        await fetch(`${API}/api/user/bookmarks/${article.slug || article.id}?email=${encodeURIComponent(lu.email)}`, { method: 'DELETE' });
                        setBookmarked(false);
                      } else {
                        await fetch(`${API}/api/user/bookmarks`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: lu.email, article_id: article.slug || article.id }) });
                        setBookmarked(true);
                      }
                    }} data-testid="bookmark-article-btn" className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all ${bookmarked ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 hover:bg-blue-50 hover:text-blue-500'}`}>
                      {bookmarked ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
                      <span className="text-xs">{bookmarked ? (globalLang === 'hr' ? 'Spremljeno' : 'Saved') : (globalLang === 'hr' ? 'Pročitaj kasnije' : 'Read Later')}</span>
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={handleWhatsAppShare} data-testid="whatsapp-share-btn" className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" title="WhatsApp">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </button>
                    <button onClick={() => handleShare('facebook')} className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"><Facebook size={14} /></button>
                    <button onClick={() => handleShare('twitter')} className="p-2 bg-sky-500 text-white rounded-full hover:bg-sky-600 transition-colors"><Twitter size={14} /></button>
                    <button onClick={() => handleShare('copy')} className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-300 transition-colors"><Link2 size={14} /></button>
                    <button onClick={() => setShowQR(!showQR)} data-testid="qr-share-btn" className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-300 transition-colors"><QrCode size={14} /></button>
                  </div>
                </div>
                {/* QR Code */}
                {showQR && (
                  <div className="mt-4 flex justify-center" data-testid="qr-code-display">
                    <img src={`${API}/api/articles/${slug}/qrcode`} alt="QR Code" className="w-40 h-40 rounded-lg border" />
                  </div>
                )}
                {/* Export buttons */}
                <div className="flex items-center gap-2 mt-4" data-testid="export-buttons">
                  <span className="text-xs text-gray-400 font-display">{globalLang === 'hr' ? 'Izvoz:' : 'Export:'}</span>
                  {['pdf', 'docx', 'txt'].map(fmt => (
                    <a key={fmt} href={`${API}/api/articles/${slug}/export/${fmt}`} target="_blank" rel="noopener noreferrer"
                      data-testid={`export-${fmt}-btn`}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-sky-accent/10 hover:text-sky-accent transition-all">
                      <Download size={10} /> {fmt.toUpperCase()}
                    </a>
                  ))}
                </div>
              </div>

              {/* In-content Ad (bottom) */}
              {!hideAds && <div className="my-8 flex justify-center">
                <AdSenseSlot slotId="post-content" width={728} height={90} />
              </div>}

              {/* Comments Section */}
              <section className="mt-8" data-testid="comments-section">
                <h3 className="font-serif text-2xl font-bold text-navy dark:text-white mb-6">
                  {globalLang === 'hr' ? 'Komentari' : 'Comments'} ({comments.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0)})
                </h3>

                {/* Add comment - login required */}
                {(isAuthenticated || !!JSON.parse(localStorage.getItem('ct_user') || 'null')) ? (
                  <form onSubmit={handleComment} className="mb-8">
                    {replyTo && (
                      <div className="flex items-center gap-2 mb-2 text-xs text-sky-accent bg-sky-accent/5 px-3 py-2 rounded-lg">
                        <Reply size={12} />
                        <span>{globalLang === 'hr' ? 'Odgovaraš na komentar' : 'Replying to comment'}</span>
                        <button type="button" onClick={() => setReplyTo(null)} className="ml-auto text-gray-400 hover:text-red-500">&times;</button>
                      </div>
                    )}
                    <div className="flex items-start gap-3">
                      {(user?.picture || JSON.parse(localStorage.getItem('ct_user') || '{}')?.picture) && (
                        <img src={user?.picture || JSON.parse(localStorage.getItem('ct_user') || '{}')?.picture} alt="" className="w-9 h-9 rounded-full flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <textarea value={newComment} onChange={e => setNewComment(e.target.value)}
                          placeholder={globalLang === 'hr' ? 'Podijelite svoje mišljenje...' : 'Share your thoughts...'}
                          rows={3} data-testid="comment-input"
                          className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:border-navy dark:bg-gray-800 dark:text-white resize-none" />
                        <div className="flex justify-end mt-2">
                          <button type="submit" disabled={!newComment.trim() || submittingComment} data-testid="submit-comment-btn"
                            className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-navy-dark disabled:opacity-50 transition-colors">
                            <Send size={14} /> {submittingComment ? '...' : (globalLang === 'hr' ? 'Objavi' : 'Post')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                ) : (
                  <div className="bg-cream dark:bg-gray-800 rounded-xl p-6 mb-6 text-center" data-testid="comment-login-required">
                    <Lock size={20} className="mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-600 dark:text-gray-400 mb-3 text-sm">
                      {globalLang === 'hr' ? 'Morate biti prijavljeni da biste komentirali' : 'You must be logged in to comment'}
                    </p>
                  </div>
                )}

                {/* Threaded comment list */}
                <div className="space-y-4">
                  {comments.map(comment => (
                    <div key={comment.id} className="space-y-2">
                      {/* Root comment */}
                      <div className="flex items-start gap-3 bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700" data-testid="comment-item">
                        {comment.author_picture ? (
                          <img src={comment.author_picture} alt={comment.author_name} className="w-9 h-9 rounded-full flex-shrink-0" />
                        ) : (
                          <div className="w-9 h-9 bg-navy rounded-full flex items-center justify-center flex-shrink-0"><User size={16} className="text-white" /></div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-medium text-navy dark:text-white text-sm">{comment.author_name || 'Anonymous'}</span>
                            {comment.badge && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                comment.badge.level === 'legend' ? 'bg-yellow-100 text-yellow-700' :
                                comment.badge.level === 'expert' ? 'bg-purple-100 text-purple-700' :
                                comment.badge.level === 'activist' ? 'bg-green-100 text-green-700' :
                                comment.badge.level === 'regular' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-500'
                              }`} data-testid="commenter-badge">{comment.badge.label}</span>
                            )}
                            <span className="text-xs text-gray-400">
                              {comment.created_at ? formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: getDateLocale(globalLang) }) : ''}
                            </span>
                            {comment.was_moderated && <span className="text-[10px] text-red-400 italic">{globalLang === 'hr' ? 'moderiran' : 'moderated'}</span>}
                          </div>
                          <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">{comment.content}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <button onClick={() => axios.post(`${API}/api/comments/${comment.id}/like`).then(() => {})} className="text-xs text-gray-400 hover:text-sky-accent flex items-center gap-1" data-testid="like-comment-btn">
                              <ThumbsUp size={11} /> {comment.likes || 0}
                            </button>
                            {(isAuthenticated || !!JSON.parse(localStorage.getItem('ct_user') || 'null')) && (
                              <button onClick={() => setReplyTo(comment.id)} className="text-xs text-gray-400 hover:text-sky-accent flex items-center gap-1" data-testid="reply-comment-btn">
                                <Reply size={11} /> {globalLang === 'hr' ? 'Odgovori' : 'Reply'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Replies */}
                      {comment.replies && comment.replies.length > 0 && (
                        <div className="ml-12 space-y-2">
                          {comment.replies.map(reply => (
                            <div key={reply.id} className="flex items-start gap-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
                              {reply.author_picture ? (
                                <img src={reply.author_picture} alt={reply.author_name} className="w-7 h-7 rounded-full flex-shrink-0" />
                              ) : (
                                <div className="w-7 h-7 bg-navy rounded-full flex items-center justify-center flex-shrink-0"><User size={12} className="text-white" /></div>
                              )}
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="font-medium text-navy dark:text-white text-xs">{reply.author_name}</span>
                                  {reply.badge && <span className="text-[9px] px-1 py-0.5 rounded-full bg-gray-100 text-gray-500">{reply.badge.label}</span>}
                                  <span className="text-[10px] text-gray-400">{reply.created_at ? formatDistanceToNow(new Date(reply.created_at), { addSuffix: true, locale: getDateLocale(globalLang) }) : ''}</span>
                                </div>
                                <p className="text-gray-600 dark:text-gray-300 text-xs leading-relaxed">{reply.content}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {comments.length === 0 && (
                    <p className="text-gray-400 text-center py-6">{globalLang === 'hr' ? 'Još nema komentara. Budite prvi!' : 'No comments yet. Be the first!'}</p>
                  )}
                </div>
              </section>
            </article>

            {/* Related Articles */}
            {related.length > 0 && (
              <section className="mt-10" data-testid="related-articles">
                <h3 className="font-serif text-2xl font-bold text-navy dark:text-white mb-5">Povezane priče</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {related.slice(0, 8).map(art => (
                    <ArticleCard key={art.id || art._id} article={art} size="small" />
                  ))}
                </div>
              </section>
            )}

            {/* Cross-Portal Content Links */}
            {crossPortalLinks.length > 0 && (
              <section className="mt-10" data-testid="cross-portal-links">
                <h3 className="font-serif text-xl font-bold text-navy dark:text-white mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 bg-sky-500 rounded-full" />
                  Više na portalu
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {crossPortalLinks.map(art => (
                    <Link key={art.slug} to={`/article/${art.slug}`}
                      className="flex gap-3 p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-sky-300 hover:shadow-sm transition-all group"
                      data-testid={`cross-portal-link-${art.slug}`}
                    >
                      {art.featured_image && (
                        <img src={art.featured_image} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="text-[10px] font-medium text-sky-500 uppercase tracking-wide">{art.category}</span>
                        <p className="text-sm font-medium text-navy dark:text-white group-hover:text-sky-500 transition-colors line-clamp-2">{art.title}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-3">
            <div className="sticky top-20">
              <Sidebar recentArticles={recentArticles} popularArticles={popularArticles} tags={tags} />
            </div>
          </div>
        </div>
      </main>

      {/* Scroll to top */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-6 right-6 p-3 bg-navy text-white rounded-full shadow-lg hover:bg-navy-dark transition-colors z-40"
        data-testid="scroll-to-top"
        aria-label="Scroll to top"
      >
        <ChevronUp size={20} />
      </button>

      <Footer />
    </div>
  );
}
